import {
  AgentSessionManager,
  ApiUriConstants,
  HttpClient,
  HttpPollingRequestInit,
  HttpUtilService,
  Logger,
  HttpPollingRequestParam,
  UtilWorker,
  IndexDBStoreNames,
  dbInstance,
  IndexDBKeyNames,
  clearIndexDbStore,
} from '@nice-ccf/core-sdk';
import { wrap } from 'comlink';
import { CXoneDirectory } from '../cxone-directory';
import { AddressBooks, AddressBooksEntries, AgentStateResponse, DirectoryResponse, SkillEvent } from '@nice-ccf/common-sdk';
import { CXoneDirectoryAdapter } from '../adapter/cxone-directory-adapter';
import { DirectoryEntities } from '@nice-ccf/common-sdk';

const NO_MATCHING_RECORDS_FOUND = "No Matching Records Found.";
const OFFSET_AND_LIMIT_VALUES_SHOULD_ALWAYS_BE_GREATER_THAN_ZERO = "Offset and Limit values should always be greater than zero.";

/**
 * Directory Provider Class
 */
export class CXoneDirectoryProvider {
  private logger: Logger = new Logger('SDK', 'CXoneDirectoryProvider');
  public pollingWorker: any;
  private agentSession = AgentSessionManager.instance;
  private baseUri = '';
  private utilService: HttpUtilService = new HttpUtilService();
  private directoryAdapter: CXoneDirectoryAdapter = new CXoneDirectoryAdapter();
  private directoryBase = <CXoneDirectory>{};
  private offset = -1; // to know the pagination offset for the directory data requested
  private limit = -1; // to know the pagination last index for the directory data requested
  private searchText = ""; // in store the current requested directory search text
  private entityPollingFlag = { // to keep track of entity which needed to be polled
    addressBookList: false,
    skillList: false,
    agentList: false
  }
  private currentEntities: DirectoryEntities[] | undefined; // to keep track of current requested entities for directory data
  private lastAddressBookSearchEntriesMap = new Map<number, number>(); // here map has addressBookEntryId as key and addressBookId as value, to keep the map of all the matched search addressBook entry which will help use to identify quickly from this search directory map while updation
  private lastAddressBookEntriesArray: Array<{ addressBookEntryId: number, addressBookId: number }> = []; // to hold all the addressBook entry which will help us in quick pagination result and also for the total addressBook count
  private entityCounts = { // to keep track of the total records for each entities for directory data response
    addressBookList: 0,
    skillList: 0,
    agentList: 0
  }


  /**
   * Used to set the directory base instance to access the subject from the base class
   * @example -
   */
  public setDirectoryBaseInstance(directoryBase: CXoneDirectory) {
    this.directoryBase = directoryBase;
  }

  /**
   * Used to handle the directory data request
   * @param isPolling - polling flag to identify if polling is needed or not
   * @param entity - requested entity array for directory data
   * @param offset - start index for the pagination data, should be greater than 0
   * @param limit - end index for the pagination data, should be greater than 0
   * @param searchText - search string in case for search request, search will happen on fields depending on entity requested.
   * If requested entity is agentList then search will happen based on fields - (firstName, lastName, userName).
   * If requested entity is skillList then search will happen based on field - (skillName).
   * If requested entity is addressBookList then search will happen based on field - (firstName, lastName, mobile, phone, email).
   * @example -
   * ```
   * getDirectoryData(true, ["agentList"], 1, 14, '');
   * ```
   */
  public async getDirectoryData(entity: DirectoryEntities[], isPolling?: boolean, offset?: number, limit?: number, searchText?: string) {
    if (offset !== undefined && limit !== undefined && (offset <= 0 || limit <= 0)) { // if offset and limit is less than equal to 0 send error response for the requested entity
      const directoryResponse: DirectoryResponse = { skillList: { data: [] }, agentList: { data: [] }, addressBookList: { data: [] } };
      if (entity.includes(DirectoryEntities.ADDRESS_BOOK_LIST)) directoryResponse.addressBookList.errorMsg = OFFSET_AND_LIMIT_VALUES_SHOULD_ALWAYS_BE_GREATER_THAN_ZERO;
      if (entity.includes(DirectoryEntities.AGENT_LIST)) directoryResponse.agentList.errorMsg = OFFSET_AND_LIMIT_VALUES_SHOULD_ALWAYS_BE_GREATER_THAN_ZERO;
      if (entity.includes(DirectoryEntities.SKILL_LIST)) directoryResponse.skillList.errorMsg = OFFSET_AND_LIMIT_VALUES_SHOULD_ALWAYS_BE_GREATER_THAN_ZERO;
      this.directoryBase.directoryEvent.next(directoryResponse);
      return;
    }
    const db = await dbInstance();
    if (searchText) {// if search text is present that means the current request for search
      const isNewSearchRequest = this.isNewSearchedRequest(searchText, offset, limit, entity);
      if (offset) this.offset = offset;
      if (limit) this.limit = limit;
      this.searchText = searchText || "";
      this.currentEntities = entity; // to keep track of the current search entities which will be needed while update
      const currentPollingNeededEntities = this.isPollingNeeded(entity);
      if (currentPollingNeededEntities?.length) {// if the polling need any of the entity requested for the search then we will first poll the data first and then send the response for the search
        if(isNewSearchRequest)await db?.delete(IndexDBStoreNames.DIRECTORY, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE);// remove previously saved search text data in case of new search request in case of polling is pending for any of the entities or the request is for without polling
        this.requestDirectoryData(isPolling, currentPollingNeededEntities);
        if (isPolling) this.updateEntityPolled(currentPollingNeededEntities);
      } else { // if no polling is needed for the entities requested then we will just take the data from the indexDB and publish the response
        let directoryResponse: DirectoryResponse = { skillList: { data: [] }, agentList: { data: [] }, addressBookList: { data: [] } };
        if (isNewSearchRequest) { // if the searchRequest is new we will not look for the data cache in indexDB
          directoryResponse = await this.searchDirectoryData(this.searchText, entity);
          await db?.put(IndexDBStoreNames.DIRECTORY, directoryResponse, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE);// to store the search data in the indexDB
        } else {
          directoryResponse = await db?.get(IndexDBStoreNames.DIRECTORY, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE) as DirectoryResponse;
        }
        this.publishDirectoryData(directoryResponse);
      }
    } else { // if no search text is present then that means the request is for polling
      this.searchText = ""; // to reset the search text to identify that while update we are not sending response as per search data
      await db?.delete(IndexDBStoreNames.DIRECTORY, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE);// to store the search data in the indexDB
      if (offset) this.offset = offset;
      if (limit) this.limit = limit;
      const currentPollingNeededEntities = this.isPollingNeeded(entity);
      this.currentEntities = entity; // to keep track of the current entities which will be needed while update
      if (isPolling) { // if polling is required 
        if (currentPollingNeededEntities?.length) {// if any entity which has'nt been polled yet
          this.requestDirectoryData(isPolling, currentPollingNeededEntities);
          this.updateEntityPolled(currentPollingNeededEntities);// to update the entityPollingFlag based on the entity polled
        } else {// if no polling entity remains then that means we just have to send the data from the db as we already have data as we have already polled for all the requested entities
          let directoryResponse: DirectoryResponse = { skillList: { data: [] }, agentList: { data: [] }, addressBookList: { data: [] } };
          const currentAddressBookList = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.ADDRESS_BOOK_LIST) as AddressBooks[];
          const currentAgentList = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.AGENT_LIST) as AgentStateResponse[];
          const currentSkillList = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.SKILL_LIST) as SkillEvent[];
          entity?.forEach((e) => {
            if (e === DirectoryEntities.ADDRESS_BOOK_LIST) {
              directoryResponse.addressBookList.data = currentAddressBookList;
              this.entityCounts.addressBookList = this.lastAddressBookEntriesArray.length || 0;
            }
            if (e == DirectoryEntities.AGENT_LIST) {
              directoryResponse.agentList.data = currentAgentList;
              this.entityCounts.agentList = currentAgentList.length || 0;
            }
            if (e == DirectoryEntities.SKILL_LIST) {
              directoryResponse.skillList.data = currentSkillList;
              this.entityCounts.skillList = currentSkillList.length || 0;
            }
          })
          if (this.offset > 0 && this.limit > 0) {
            directoryResponse = await this.handleDirectoryPagination(directoryResponse);
          }
          this.publishDirectoryData(directoryResponse);
        }
      } else { // when polling is no required
        this.requestDirectoryData(isPolling, entity);
      }
    }
  }

  
  /**
   * Used to clear the directory store data from the index db
   * @example - 
   * ```
   * clearDirectoryDB();
   * ```
   */
   public async clearDirectoryDB(){
    await clearIndexDbStore(IndexDBStoreNames.DIRECTORY);
  }

  /**
   * Used to check if the current request is a new search request or the already searched request
   * @param newSearchText - search string
   * @param newOffset - start index for pagination
   * @param newLimit - last index for pagination
   * @param newEntity - entity array
   * @returns boolean stating if true that means its a new search request
   */
  private isNewSearchedRequest(newSearchText?: string, newOffset?: number, newLimit?: number, newEntity?: DirectoryEntities[]) {
    return this.searchText !== newSearchText || this.offset !== newOffset || this.limit !== newLimit || newEntity?.sort()?.join('') !== this.currentEntities?.sort()?.join('');
  }

  /**
   * Used to check from the requested entity to find out if any of the entity is still needed to be polled and if so returns that entity
   * @param entity - directory entity array
   */
  private isPollingNeeded(entity?: DirectoryEntities[]) {
    const entities: DirectoryEntities[] = [];
    const allEntity = [DirectoryEntities.ADDRESS_BOOK_LIST, DirectoryEntities.AGENT_LIST, DirectoryEntities.SKILL_LIST];
    if (!entity) {
      allEntity.forEach(e => {
        !this.entityPollingFlag[e] && entities.push(e);
      })
    } else {
      entity.forEach(e => {
        !this.entityPollingFlag[e] && entities.push(e);
      })
    }
    return entities;
  }

  /**
   * Used to update the flag based on the entity array passed, this will ensure that the passed entities has been already polled
   * @param entity - directory entity array
   */
  private updateEntityPolled(entity?: DirectoryEntities[]) {
    if (!entity) {
      this.entityPollingFlag[DirectoryEntities.ADDRESS_BOOK_LIST] = true;
      this.entityPollingFlag[DirectoryEntities.AGENT_LIST] = true;
      this.entityPollingFlag[DirectoryEntities.SKILL_LIST] = true;
    } else {
      entity.forEach(e => {
        if (!this.entityPollingFlag[e]) {
          this.entityPollingFlag[e] = true;
        }
      })
    }
  }

  /**
     * Used to get the search result for the directory entities from indexDB. if entities array is passed then the result will be included for those entities 
     * if not passed then result will include all the entities
     * @param searchText - text on the basis of which the search will be performed
     * @param entities - array of requested entity options for search
     */
  private async searchDirectoryData(searchText: string, entities?: string[]) {
    const db = await dbInstance();
    const searchTextUpperCase = searchText.toUpperCase();
    const directoryResponse: DirectoryResponse = { skillList: { data: [] }, agentList: { data: [] }, addressBookList: { data: [] } };
    const AgentList: AgentStateResponse[] = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.AGENT_LIST) as Array<AgentStateResponse> || [];
    const skillList: SkillEvent[] = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.SKILL_LIST) as Array<SkillEvent> || [];
    const addressBookList: AddressBooks[] = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.ADDRESS_BOOK_LIST) as Array<AddressBooks> || [];
    let addressBookResultState: AddressBooks[] = [], agentResultState: AgentStateResponse[] = [], skillResultState: SkillEvent[] = [];
    if (entities?.length) {
      entities.forEach(entity => {
        switch (entity) {
          case DirectoryEntities.ADDRESS_BOOK_LIST:
            addressBookResultState = this.filterAddressBook(searchTextUpperCase, addressBookList);
            break;
          case DirectoryEntities.AGENT_LIST:
            agentResultState = this.getFilteredAgentList(searchTextUpperCase, AgentList);
            break;
          case DirectoryEntities.SKILL_LIST:
            skillResultState = this.getFilteredSkillList(searchTextUpperCase, skillList);
            break;
        }
      })
    } else {
      addressBookResultState = this.filterAddressBook(searchTextUpperCase, addressBookList);
      agentResultState = this.getFilteredAgentList(searchTextUpperCase, AgentList);
      skillResultState = this.getFilteredSkillList(searchTextUpperCase, skillList);
    }
    directoryResponse.agentList.data = agentResultState;
    directoryResponse.skillList.data = skillResultState;
    directoryResponse.addressBookList.data = addressBookResultState;
    return directoryResponse;
  }

  /**
   * Used to handle the agentList pagination for the search result if the search text is matched and then returns only the data based on offset and limit
   * @param searchText - search string
   * @param agentList - array of agentList
   */
  private getFilteredAgentList(searchText: string, agentList: AgentStateResponse[]) {
    let agentResultState: AgentStateResponse[] = [];
    let matchedCount = 0;
    if (this.offset > 0 && this.limit > 0) {
      for (let i = 0; i < agentList.length; i++) {
        const currentAgent = agentList[i];
        if (currentAgent.firstName.toUpperCase().includes(searchText) || currentAgent.lastName.toUpperCase().includes(searchText) || currentAgent.userName.toUpperCase().includes(searchText)) {
          if (matchedCount >= this.offset - 1 && matchedCount < this.limit + this.offset - 1) {
            agentResultState.push(agentList[i]);
          }
          matchedCount++;
        }
      }
    } else {
      agentResultState = agentList.filter(list => list.firstName.toUpperCase().includes(searchText) || list.lastName.toUpperCase().includes(searchText) || list.userName.toUpperCase().includes(searchText));
      matchedCount = agentResultState.length || 0;
    }
    this.entityCounts.agentList = matchedCount;
    return agentResultState
  }

  /**
   * Used to handle the skillList pagination for the search result if the search text is matched and then returns only the data based on offset and limit
   *  @param searchText - search string
   * @param agentList - array of agentList
   */
  private getFilteredSkillList(searchText: string, skillList: SkillEvent[]) {
    let skillResultState: SkillEvent[] = [];
    let matchedCount = 0;
    if (this.offset > 0 && this.limit > 0) {
      for (let i = 0; i < skillList.length; i++) {
        const currentSkill = skillList[i];
        if (currentSkill.skillName.toUpperCase().includes(searchText)) {
          if (matchedCount >= this.offset - 1 && matchedCount < this.limit + this.offset - 1) {
            skillResultState.push(skillList[i]);
          }
          matchedCount++;
        }
      }
    } else {
      skillResultState = skillList.filter(list => list.skillName.toUpperCase().includes(searchText));
      matchedCount = skillResultState.length || 0;
    }
    this.entityCounts.skillList = matchedCount;
    return skillResultState;
  }

  /**
  * Used to filter the data from the address book List on the basis of search text and handle the pagination based on the offset and limit if requested
  * @param searchText - string value on which search will be performed
  * @param addressBookList - list of addressBook
  */
  private filterAddressBook(searchText: string, addressBookList: AddressBooks[]): AddressBooks[] {
    const newAddressBookList: AddressBooks[] = [];
    this.lastAddressBookSearchEntriesMap = new Map<number, number>();
    let matchCount = 0;
    if (this.offset > 0 && this.limit > 0) { // if pagination offset and limit is defined
      addressBookList?.forEach((addressBook: AddressBooks) => {
        const newAddressBook: AddressBooks = { ...addressBook, addressBookEntries: [] };
        const filteredAddressBookEntries = addressBook.addressBookEntries?.filter(list => list.firstName.toUpperCase().includes(searchText) || list.lastName.toUpperCase().includes(searchText) || list.mobile.includes(searchText) || list.phone.includes(searchText) || list.email.toUpperCase().includes(searchText));
        if (filteredAddressBookEntries?.length) {
          filteredAddressBookEntries.forEach(addressBookEntry => {
            if (matchCount >= this.offset - 1 && matchCount < this.limit + this.offset - 1) {
              newAddressBook?.addressBookEntries?.push(addressBookEntry);
              this.lastAddressBookSearchEntriesMap.set(addressBookEntry.addressBookEntryId, addressBook.addressBookId);
            }
            matchCount++;
          })
          if (newAddressBook?.addressBookEntries?.length) newAddressBookList.push(newAddressBook);
        }
      })
    } else {
      addressBookList?.forEach((addressBook: AddressBooks) => {
        if (addressBook?.addressBookEntries?.length) {
          const newAddressBook: AddressBooks = { ...addressBook, addressBookEntries: [] };
          const filteredAddressBookEntries = addressBook.addressBookEntries?.filter(list => list.firstName.toUpperCase().includes(searchText) || list.lastName.toUpperCase().includes(searchText) || list.mobile.includes(searchText) || list.phone.includes(searchText) || list.email.toUpperCase().includes(searchText));
          newAddressBook.addressBookEntries = filteredAddressBookEntries;
          if (filteredAddressBookEntries.length) {
            filteredAddressBookEntries.forEach(addressBookEntry => {
              this.lastAddressBookSearchEntriesMap.set(addressBookEntry.addressBookEntryId, addressBook.addressBookId);
            })
            if (newAddressBook?.addressBookEntries?.length) newAddressBookList.push(newAddressBook);
          }
        }
      });
      matchCount = this.lastAddressBookSearchEntriesMap?.size || 0;
    }
    this.entityCounts.addressBookList = matchCount;
    return newAddressBookList;
  }

  /**
   * Used to initiate the polling for directory data
   * @example -
   * ```
   * const directoryProvider = new CXoneDirectoryProvider();
   * this.directoryProvider.startPolling();
   * ```
   */
  private requestDirectoryData(
    isPolling?: boolean,
    entity = [
      DirectoryEntities.AGENT_LIST,
      DirectoryEntities.SKILL_LIST,
      DirectoryEntities.ADDRESS_BOOK_LIST,
    ]
  ) {
    this.logger.info('startPolling', 'startPolling in CXoneDirectoryProvider');
    const requests: Array<HttpPollingRequestParam> = [];
    this.baseUri = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    entity = entity?.length
      ? entity
      : [DirectoryEntities.AGENT_LIST, DirectoryEntities.SKILL_LIST, DirectoryEntities.ADDRESS_BOOK_LIST];
    if (this.baseUri && authToken) {
      const { startPolling } = this.initUtilWorker();
      if (entity.includes(DirectoryEntities.AGENT_LIST)) {
        const agentStateUrl = new URL(ApiUriConstants.AGENT_STATE_URI, this.baseUri);
        agentStateUrl.searchParams.set(
          'fields',
          'agentId,agentStateName,contactId,firstName,isActive,isOutbound,lastName,lastPollTime,lastUpdateTime,mediaName,outStateCode,outStateDescription,skillId,skillName,startDate,stationPhoneNumber,teamId,teamName,userName'
        );
        agentStateUrl.searchParams.set('updatedSince', new Date(0).toISOString());
        const agentStateRequest: HttpPollingRequestInit = {
          headers: this.utilService.initPollingHeader(authToken, 'application/json'),
          method: 'GET',
        };
        requests.push({
          url: agentStateUrl.toString(),
          request: agentStateRequest,
          key: DirectoryEntities.AGENT_LIST,
        });
      }
      if (entity.includes(DirectoryEntities.SKILL_LIST)) {
        const skillUrl = new URL(ApiUriConstants.SKILL_CACHE_URI, this.baseUri);
        skillUrl.searchParams.set('fields', 'IsActive,IsOutbound,MediaType,MediaName,LastUpdateTime,SkillId,SkillName');
        skillUrl.searchParams.set('updatedSince', new Date(0).toISOString());
        const skillRequest: HttpPollingRequestInit = {
          headers: this.utilService.initPollingHeader(authToken, 'application/json'),
          method: 'GET',
        };
        requests.push({ url: skillUrl.toString(), request: skillRequest, key: DirectoryEntities.SKILL_LIST });
      }
      if (entity.includes(DirectoryEntities.ADDRESS_BOOK_LIST)) {
        const user = this.agentSession.auth.getUserDetails();
        const agentId = user.icAgentId;
        const addressBooksUrl = new URL(
          ApiUriConstants.ADDRESS_BOOK_URI.replace('{agentId}', agentId),
          this.baseUri
        );
        addressBooksUrl.searchParams.set('fields', 'includeEntries,updatedSince');
        addressBooksUrl.searchParams.set('includeEntries', 'true');
        addressBooksUrl.searchParams.set('updatedSince', new Date(0).toISOString());
        const addressBookRequest: HttpPollingRequestInit = {
          headers: this.utilService.initPollingHeader(authToken, 'application/json'),
          method: 'GET',
        };
        requests.push({
          url: addressBooksUrl.toString(),
          request: addressBookRequest,
          key: DirectoryEntities.ADDRESS_BOOK_LIST,
        });
      }

      HttpClient.getWithPolling(requests, this.handleDirectoryResponse.bind(this), startPolling, {
        isPolling: isPolling,
      });
    }
  }

  /**
   * Used to terminate the polling of agent state
   * @example -
   * ```
   * const directoryProvider = new CXoneDirectoryProvider();
   * this.directoryProvider.terminatePolling();
   * ```
   */
  public terminatePolling() {
    this.terminateDirectoryWorker();
    // after polling is terminated then we need to update the entity polling flag to false for all entities
    this.entityPollingFlag[DirectoryEntities.ADDRESS_BOOK_LIST] = false;
    this.entityPollingFlag[DirectoryEntities.AGENT_LIST] = false;
    this.entityPollingFlag[DirectoryEntities.SKILL_LIST] = false;
  }

  /**
   * Callback method which will passed on to the worker and will be executed after the polling api response
   * then will publish to the subject subscriber with the directory data
   * @param response - agent state api response object
   * @example -
   * ```
   * handleDirectoryResponse(data);
   * ```
   */
  public async handleDirectoryResponse(response: any) {
    const db = await dbInstance();
    let isUpdate = true;// this flag will let us know if the data is for updation or for the first time response after initial polling response
    if (response) {
      let directoryEvent: DirectoryResponse = await this.directoryAdapter.handleEvent(response);
      if (directoryEvent?.agentList.data.length || directoryEvent.addressBookList.data.length || directoryEvent.skillList.data.length) {
        const { skillList, agentList, addressBookList } = { ...directoryEvent };
        const updatedDirectoryResponse: DirectoryResponse = { skillList: { data: [] }, agentList: { data: [] }, addressBookList: { data: [] } };
        //to update the new directory values in indexDB
        updatedDirectoryResponse.skillList.data = (skillList.data.length) ? await this.updateSkillListInDB(skillList.data) : await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.SKILL_LIST) as SkillEvent[];
        updatedDirectoryResponse.agentList.data = (agentList.data.length) ? await this.updateAgentListInDB(agentList.data) : await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.AGENT_LIST) as AgentStateResponse[];
        updatedDirectoryResponse.addressBookList.data = (addressBookList.data.length) ? await this.updateAddressBookListInDB(addressBookList.data) : await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.ADDRESS_BOOK_LIST) as AddressBooks[];;
        if (this.searchText && this.currentEntities?.length) {//if searchText is present then the directory response for search
          const currentSearchData: DirectoryResponse = await db?.get(IndexDBStoreNames.DIRECTORY, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE) as DirectoryResponse;
          if (!currentSearchData) { // if no searchData is present in the indexDB that means its the first time we are searching for the searchText due to polling not been done earlier
            directoryEvent = await this.searchDirectoryData(this.searchText, this.currentEntities);
            isUpdate= false;
            await db?.put(IndexDBStoreNames.DIRECTORY, directoryEvent, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE);
          } else {// else searchData is already their so we will just update the data in index DB
            const newSearchData: DirectoryResponse = { skillList: { data: [] }, agentList: { data: [] }, addressBookList: { data: [] } };
            this.currentEntities.forEach(entity => {
              if (entity === DirectoryEntities.AGENT_LIST && directoryEvent?.agentList?.data?.length) {
                newSearchData.agentList.data = this.updateSearchAgentList(currentSearchData.agentList.data, directoryEvent.agentList.data);
              }
              if (entity === DirectoryEntities.SKILL_LIST && directoryEvent?.skillList?.data?.length) {
                newSearchData.skillList.data = this.updateSearchSkillList(currentSearchData.skillList.data, directoryEvent.skillList.data);
              }
              if (entity === DirectoryEntities.ADDRESS_BOOK_LIST && directoryEvent?.addressBookList?.data?.length) {
                newSearchData.addressBookList.data = this.updateAddressBookList(directoryEvent.addressBookList.data);
              }
            })
            await db?.put(IndexDBStoreNames.DIRECTORY, currentSearchData, IndexDBKeyNames.DIRECTORY_SEARCH_RESPONSE);
            directoryEvent = currentSearchData;
          }
        } else { // if search text is not present that means then the response should be for all the data set
          if (this.currentEntities?.length) {
            isUpdate= false
            this.currentEntities.forEach(e => {
              if (e === DirectoryEntities.AGENT_LIST) directoryEvent.agentList.data = updatedDirectoryResponse.agentList.data;
              if (e === DirectoryEntities.ADDRESS_BOOK_LIST) directoryEvent.addressBookList.data = updatedDirectoryResponse.addressBookList.data;
              if (e === DirectoryEntities.SKILL_LIST) directoryEvent.skillList.data = updatedDirectoryResponse.skillList.data;
            })
          }
          if (this.offset > 0 && this.limit > 0) { // to handle pagination
            directoryEvent = await this.handleDirectoryPagination(directoryEvent);
          }
        }
        this.publishDirectoryData(directoryEvent, isUpdate);
      }
    }
  }

  /**
   * Used to publish the directory response to the subscriber
   * also updating the total record for each entity before publishing
   * @param directoryResponse - directory response which needs to be published
   * @param isUpdate - this boolean flag will help us to identify if the request is for update or not
   */
  private publishDirectoryData(directoryResponse: DirectoryResponse, isUpdate = false) {
    if (!isUpdate || directoryResponse?.agentList?.data?.length || directoryResponse?.addressBookList?.data?.length || directoryResponse?.skillList?.data?.length) {
      directoryResponse.agentList.totalRecords = this.entityCounts.agentList;
      directoryResponse.skillList.totalRecords = this.entityCounts.skillList;
      directoryResponse.addressBookList.totalRecords = this.entityCounts.addressBookList;
      if (this.currentEntities?.length) {
        if (this.currentEntities.includes(DirectoryEntities.ADDRESS_BOOK_LIST) && !directoryResponse.addressBookList.data.length) directoryResponse.addressBookList.errorMsg = NO_MATCHING_RECORDS_FOUND
        if (this.currentEntities.includes(DirectoryEntities.AGENT_LIST) && !directoryResponse.agentList.data.length) directoryResponse.agentList.errorMsg = NO_MATCHING_RECORDS_FOUND
        if (this.currentEntities.includes(DirectoryEntities.SKILL_LIST) && !directoryResponse.skillList.data.length) directoryResponse.skillList.errorMsg = NO_MATCHING_RECORDS_FOUND
        if (!this.searchText) this.currentEntities = [];
      }
      this.directoryBase.directoryEvent.next(directoryResponse);
    }
  }

  /**
   * Used to update the addressBook list for search response matching new Response from the polling update with the existing search response saved in the index db
   * @param newAddressBooks -new response for the updated addressBook data
   */
  private updateAddressBookList(newAddressBooks: AddressBooks[]) {
    const newAddressBookList: AddressBooks[] = [];
    newAddressBooks.forEach(addressBook => {
      const newAddressBook: AddressBooks = { ...addressBook, addressBookEntries: [] };
      addressBook?.addressBookEntries?.forEach(addressBookEntry => {
        if (this.lastAddressBookSearchEntriesMap.has(addressBookEntry.addressBookEntryId)) {
          newAddressBook?.addressBookEntries?.push(addressBookEntry);
        }
      })
      if (newAddressBook?.addressBookEntries?.length) newAddressBookList.push(newAddressBook);
    })
    return newAddressBookList;
  }

  /**
   * Used to update the agent list for search response matching new Response from the polling update with the existing search response saved in the index db
   * @param currentSearchAgentList - saved agent list from the index db
   * @param newAgentList - new response for the updated addressBook data
   */
  private updateSearchAgentList(currentSearchAgentList: AgentStateResponse[], newAgentList: AgentStateResponse[]) {
    newAgentList.forEach(newAgent => {
      const matchAgentIndex = currentSearchAgentList.findIndex(currentAgent => currentAgent.agentId == newAgent.agentId);
      if (matchAgentIndex >= 0) currentSearchAgentList[matchAgentIndex] = newAgent;
    })
    return currentSearchAgentList;
  }

  /**
   * Used to update the skill list for search response matching new Response from the polling update with the existing search response saved in the index db
   * @param currentSearchSkillList - saved skill list from the index db
   * @param newSkillList - new response for the updated addressBook data
   */
  private updateSearchSkillList(currentSearchSkillList: SkillEvent[], newSkillList: SkillEvent[]) {
    newSkillList.forEach(newSkill => {
      const matchSkillIndex = currentSearchSkillList.findIndex(currentAgent => currentAgent.skillId == newSkill.skillId);
      if (matchSkillIndex >= 0) currentSearchSkillList[matchSkillIndex] = newSkill;
    })
    return currentSearchSkillList;
  }

  /**
   * Used to handle the pagination based on the offset and limit in case of normal directory request flow without search request
   * @param directoryResponse - directory response object
   */
  private async handleDirectoryPagination(directoryResponse: DirectoryResponse) {
    const db = await dbInstance();
    const currentAgentList: AgentStateResponse[] = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.AGENT_LIST) as Array<AgentStateResponse>;
    const currentSkillList: SkillEvent[] = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.SKILL_LIST) as Array<SkillEvent>;
    const currentAddressBookList: AddressBooks[] = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.ADDRESS_BOOK_LIST) as Array<AddressBooks>;
    let agentList: AgentStateResponse[] = [];
    let skillList: SkillEvent[] = [];
    const paginatedAddressEntry = new Map<number, number>();
    for (let i = this.offset - 1; i < (this.limit + this.offset - 1); i++) {// to filter the data as per the pagination offset and limit from the data set
      directoryResponse.agentList?.data?.length && currentAgentList?.length && currentAgentList[i] && agentList.push(currentAgentList[i]);
      directoryResponse.skillList?.data?.length && currentSkillList?.length && currentSkillList[i] && skillList.push(currentSkillList[i]);
      directoryResponse.addressBookList?.data?.length && currentAddressBookList?.length && this.lastAddressBookEntriesArray[i] && paginatedAddressEntry.set(this.lastAddressBookEntriesArray[i]?.addressBookEntryId, this.lastAddressBookEntriesArray[i].addressBookId)
    }
    if (directoryResponse.agentList?.data?.length && currentAgentList.length && agentList.length) { // in case of agent list updation we will check if the updated data is present in the search option if so then only we will publish that data
      const newAgentList: AgentStateResponse[] = [];
      directoryResponse.agentList?.data?.forEach(agent => {
        const matchedAgentIndex = agentList.findIndex(e => e.agentId == agent.agentId);
        if (matchedAgentIndex >= 0) newAgentList.push(agent);
      });
      agentList = newAgentList;
    }
    if (directoryResponse.skillList?.data?.length && currentSkillList.length && skillList.length) { // in case of skill list updation we will check if the updated data is present in the search option if so then only we will publish that data
      const newSkillList: SkillEvent[] = [];
      directoryResponse.skillList?.data?.forEach(skill => {
        const matchedSkillIndex = skillList.findIndex(e => e.skillId == skill.skillId);
        if (matchedSkillIndex >= 0) newSkillList.push(skill);
      });
      skillList = newSkillList;
    }
    const addressBookList: AddressBooks[] = [];
    if (directoryResponse.addressBookList?.data?.length && currentAddressBookList.length && paginatedAddressEntry?.size) {
      directoryResponse.addressBookList?.data?.forEach(addressBook => {
        const newAddressBook: AddressBooks = { ...addressBook, addressBookEntries: [] };
        addressBook?.addressBookEntries?.forEach(addressBookEntry => {
          if (paginatedAddressEntry.has(addressBookEntry.addressBookEntryId)) {
            newAddressBook?.addressBookEntries?.push(addressBookEntry);
          }
        })
        if (newAddressBook?.addressBookEntries?.length) addressBookList.push(newAddressBook);
      })
    }
    directoryResponse.agentList.data = agentList;
    directoryResponse.skillList.data = skillList;
    directoryResponse.addressBookList.data = addressBookList;
    return directoryResponse;
  }

  /**
  * Used to update the skill list in index DB as per the new list
  * @param SkillList - new skill list response
  */
  private async updateSkillListInDB(SkillList: SkillEvent[]) {
    const db = await dbInstance();
    let currentSkillList = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.SKILL_LIST) as Array<SkillEvent> || [];
    if (currentSkillList?.length) {
      SkillList.forEach((skill, index) => {
        const matchedSkillIndex = currentSkillList.findIndex((currentSkill: SkillEvent) => currentSkill.skillId == skill.skillId);
        if (matchedSkillIndex >= 0) currentSkillList[matchedSkillIndex] = SkillList[index];
        else currentSkillList.push(skill);
      })
    } else {
      currentSkillList = SkillList;
    }
    db?.put(IndexDBStoreNames.DIRECTORY, currentSkillList, DirectoryEntities.SKILL_LIST);
    if (!this.searchText) this.entityCounts.skillList = currentSkillList?.length || 0; // to keep track of skill list records count
    return currentSkillList;
  }

  /**
   * Used to update the agent list in index DB as per the new list
   * @param agentList - new agent list response
   */
  private async updateAgentListInDB(agentList: AgentStateResponse[]) {
    const db = await dbInstance();
    let currentAgentList = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.AGENT_LIST) as Array<AgentStateResponse> || [];
    if (currentAgentList?.length) {
      agentList.forEach((agentState, index) => {
        const matchedAgentStateIndex = currentAgentList.findIndex((currentAgentState: AgentStateResponse) => currentAgentState.agentId == agentState.agentId);
        if (matchedAgentStateIndex >= 0) currentAgentList[matchedAgentStateIndex] = agentList[index];
        else currentAgentList.push(agentState);
      })
    } else {
      currentAgentList = agentList;
    }
    db?.put(IndexDBStoreNames.DIRECTORY, currentAgentList, DirectoryEntities.AGENT_LIST);
    if (!this.searchText) this.entityCounts.agentList = currentAgentList?.length || 0; // to keep track of agent list records count
    return currentAgentList;
  }

  /**
   * Used to update the address list in index DB as per the new list
   * @param addressBookList - new address book list response
   */
  private async updateAddressBookListInDB(addressBookList: AddressBooks[]) {
    const db = await dbInstance();
    this.lastAddressBookEntriesArray = [];
    let currentAddressBookList = await db?.get(IndexDBStoreNames.DIRECTORY, DirectoryEntities.ADDRESS_BOOK_LIST) as Array<AddressBooks> || [];
    if (currentAddressBookList?.length) {
      addressBookList.forEach((addressBook) => {
        const addressBookEntries = addressBook?.addressBookEntries || [];
        const matchedAddressBookIndex = currentAddressBookList.findIndex((currentAddressBook: AddressBooks) => currentAddressBook.addressBookId == addressBook.addressBookId);
        if (matchedAddressBookIndex >= 0) {
          if (addressBookEntries.length) {
            const currentAddressBookEntries = currentAddressBookList[matchedAddressBookIndex]?.addressBookEntries || [];
            if (currentAddressBookList?.length) {
              addressBookEntries.forEach((addressBookEntry, index) => {
                const matchedAddressBookEntryIndex = currentAddressBookEntries.findIndex((currentAddressBookEntry: AddressBooksEntries) => currentAddressBookEntry.addressBookEntryId == addressBookEntry.addressBookEntryId);
                if (matchedAddressBookEntryIndex >= 0) currentAddressBookEntries[matchedAddressBookEntryIndex] = addressBookEntries[index];
                else currentAddressBookEntries.push(addressBookEntry);
              })
              currentAddressBookList[matchedAddressBookIndex].addressBookEntries = currentAddressBookEntries;
            } else {
              currentAddressBookList[matchedAddressBookIndex].addressBookEntries = addressBookEntries;
            }
          }
        } else {
          currentAddressBookList.push(addressBook);
        }
      })
    } else {
      currentAddressBookList = addressBookList;
    }
    addressBookList.forEach(addressBook => {
      addressBook?.addressBookEntries?.forEach(addressBookEntry => {
        this.lastAddressBookEntriesArray.push({ addressBookEntryId: addressBookEntry.addressBookEntryId, addressBookId: addressBook.addressBookId });
      })
    })
    if (!this.searchText) this.entityCounts.addressBookList = this.lastAddressBookEntriesArray?.length || 0; // to keep track of addressBook list records count
    db?.put(IndexDBStoreNames.DIRECTORY, currentAddressBookList, DirectoryEntities.ADDRESS_BOOK_LIST);
    return currentAddressBookList;
  }

  /**
   * Use to initializing the util worker and will return the method inside the worker
   * @example
   * ```
   * this.initDirectoryWorker();
   * ```
   */
  public initUtilWorker() {
    if (!this.pollingWorker)
      this.pollingWorker = new Worker('../../../../../core-sdk/src/lib/worker/util-worker', {
        name: 'ccf-directory-polling-worker',
        type: 'module',
      });
    return wrap<UtilWorker>(this.pollingWorker);
  }

  /**
   * Use to terminate the directory worker
   * @example -
   * ```
   * this.terminateDirectoryWorker
   * ```
   */
  private terminateDirectoryWorker() {
    this.pollingWorker.terminate();
    this.pollingWorker = undefined;
  }
}
